import React from 'react';
import { Card, Divider, Grid, Group, Stack, Text } from '@mantine/core';
import type { ICotizacionPostventa } from '../../../modelos/CotizadorPostventa/entidades/ICotizacionPostventa';
import type { ICostosPorCategoria, IResultadoCotizacionPostventa } from '../../../modelos/CotizadorPostventa/entidades/IResultadoCotizacionPostventa';
import { formatoMoneda } from '../interfaces';

interface DesgloseCostosPostventaVistaProps {
    cotizacion: ICotizacionPostventa;
    resultado: IResultadoCotizacionPostventa;
}

const CATEGORIAS: { categoria: keyof ICostosPorCategoria; etiqueta: string }[] = [
    { categoria: 'Repuestos', etiqueta: 'Repuestos' },
    { categoria: 'ManoObra', etiqueta: 'Mano de obra' },
    { categoria: 'Transporte', etiqueta: 'Transporte' },
    { categoria: 'Viaticos', etiqueta: 'Viáticos' },
    { categoria: 'KnowHow', etiqueta: 'Know-how' },
    { categoria: 'Otros', etiqueta: 'Otros costos' }
];

const Fila: React.FC<{ etiqueta: string; valor: string; negrita?: boolean; sangria?: boolean }> = ({ etiqueta, valor, negrita, sangria }) => (
    <Group justify="space-between">
        <Text size="sm" fw={negrita ? 700 : undefined} c={sangria ? 'dimmed' : undefined} pl={sangria ? 'md' : undefined}>{etiqueta}</Text>
        <Text size="sm" fw={negrita ? 700 : 500}>{valor}</Text>
    </Group>
);

const Indicador: React.FC<{ titulo: string; valor: string; color?: string }> = ({ titulo, valor, color }) => (
    <Card withBorder radius="md" p="sm">
        <Text size="xs" c="dimmed" fw={600}>{titulo}</Text>
        <Text size="lg" fw={700} c={color}>{valor}</Text>
    </Card>
);

/** Uso interno (hoja COSTO): costo por categoría y cómo se llega al precio. */
export const DesgloseCostosPostventaVista: React.FC<DesgloseCostosPostventaVistaProps> = ({ cotizacion, resultado }) => (
    <Stack gap="md">
        <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}><Indicador titulo="Costo por unidad" valor={formatoMoneda(resultado.TotalCostoUnitario)} /></Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}><Indicador titulo="Precio por unidad" valor={formatoMoneda(resultado.PrecioVentaUnitario)} color="blue" /></Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}><Indicador titulo="Margen bruto" valor={`${resultado.MargenBruto.toLocaleString('es-CO', { maximumFractionDigits: 1 })} %`} color="green" /></Grid.Col>
        </Grid>
        <Card withBorder radius="md" p="md" maw={640}>
            <Stack gap={6}>
                <Text fw={600} size="sm">Costos directos por unidad</Text>
                {CATEGORIAS.map(({ categoria, etiqueta }) => (
                    <Fila key={categoria} etiqueta={etiqueta} valor={formatoMoneda(resultado.Costos[categoria])} sangria />
                ))}
                <Divider variant="dashed" />
                <Fila etiqueta="Total costo por unidad" valor={formatoMoneda(resultado.TotalCostoUnitario)} negrita />
                <Fila etiqueta="+ Costo de importación origen hasta obra" valor={formatoMoneda(cotizacion.CostoImportacion)} sangria />
                <Fila etiqueta="× Factor precio" valor={String(cotizacion.FactorPrecio)} sangria />
                <Divider variant="dashed" />
                <Fila etiqueta="Precio por unidad" valor={formatoMoneda(resultado.PrecioVentaUnitario)} negrita />
                <Fila etiqueta="× Número de unidades" valor={String(cotizacion.NumeroUnidades)} sangria />
                <Fila etiqueta="Precio total del proyecto" valor={formatoMoneda(resultado.PrecioVentaTotal)} negrita />
            </Stack>
        </Card>
    </Stack>
);
